import { useEffect } from "react";
import { useNavigate, useLocation } from "@remix-run/react";
import { useRoot } from "../context/RootContext";
import {
  ROUTES,
  pushRouteToStack,
  clearStackAndPush,
  getTopNonExecutionRoute,
  isReviewAboveHomeOrSyllabus,
  isExecutionRoute,
  isRestrictedRoute,
} from "../utils/navigationUtils";

export const useCustomNavigation = (route, isTaskCompleted = false) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isStandalone } = useRoot();

  useEffect(() => {
    if (isStandalone) {
      return;
    }

    // Handle navigation stack
    if (isRestrictedRoute(route)) {
      clearStackAndPush(route);
    } else {
      pushRouteToStack(route);
    }

    const handlePopState = (e) => {
      e.preventDefault();

      if (route === ROUTES.HOME) {
        // For home route, prevent back navigation
        window.history.pushState(null, "", location.pathname);
        return;
      }

      if (route === ROUTES.SYLLABUS) {
        // For syllabus route, always navigate to HOME
        navigate(ROUTES.HOME, { replace: true });
        return;
      }

      if (route === ROUTES.REVIEW) {
        // For review route, navigate to the top non-execution route or HOME
        const topRoute = getTopNonExecutionRoute();
        if (topRoute) {
          navigate(topRoute, { replace: true });
        } else {
          navigate(ROUTES.HOME, { replace: true });
        }
        return;
      }

      if (isExecutionRoute(route)) {
        if (isTaskCompleted) {
          // If task is completed, always go to home
          navigate(ROUTES.HOME, { replace: true });
        } else if (isReviewAboveHomeOrSyllabus()) {
          // If REVIEW is above HOME/SYLLABUS, go to review
          navigate(ROUTES.REVIEW, { replace: true });
        } else {
          // Otherwise go to the top non-execution route or home
          const topRoute = getTopNonExecutionRoute();
          if (topRoute) {
            navigate(topRoute, { replace: true });
          } else {
            navigate(ROUTES.HOME, { replace: true });
          }
        }
        return;
      }
    };

    if (isRestrictedRoute(route)) {
      window.history.pushState(null, "", location.pathname);
    } else {
      window.history.replaceState(null, "", location.pathname);
    }

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [route, isTaskCompleted, location.pathname, navigate, isStandalone]);
};
